import {
    getBillingInvoiceGetBillingInvoiceStatuses,
    getBillingInvoiceGetLastInvoiceUpdatedDate,
    postBillingInvoiceRerunInvoiceProcessing,
    postBillingInvoiceSaveBillingInvoiceAdjustments,
    postBillingInvoiceUpdateBillingInvoiceStatus,
} from '@apis/Invoices';
import {
    BillingInvoice,
    BillingInvoiceAdjustment,
    BillingInvoiceAdjustmentAdjustmentOperation,
    BillingInvoiceAdjustmentPlatform,
    BillingInvoiceStatus,
    BillingInvoiceStatusProperty,
    InvoiceProcessingLogEntry,
    NamedFilterSet,
} from '@apis/Invoices/model';
import {
    ActionIcon,
    Box,
    Button,
    Checkbox,
    Grid,
    Group,
    LoadingOverlay,
    Menu,
    Modal,
    NumberInput,
    Select,
    Space,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { useCallback, useEffect, useState, ReactNode, useMemo } from 'react';
import { BillingInvoiceAdminPanelModel } from './BillingInvoiceAdminPanelModel';
import {
    CaretRight,
    ChevronDown,
    ChevronRight,
    Copy,
    Dots,
    DotsVertical,
    Download,
    Eye,
    FileUpload,
    Plus,
    Refresh,
    Upload,
} from 'tabler-icons-react';
import { useDi, useDiComponent } from '@root/Services/DI';
import { FormatService, useFmtSvc } from '@root/Services/FormatService';
import {
    SettingsInputRow,
    SettingsInputStack,
    SettingsInteractiveRow,
    SettingsLabel,
    SettingsSection,
    SettingsSectionBodyDivider,
    SettingsSectionItem,
    SettingsSectionItemBody,
    SettingsSectionItemHeader,
    SettingsSectionItemHeaderLabel,
} from '@root/Design/Settings';
import { Trash, ArrowUp, ArrowDown } from 'tabler-icons-react';
import { ApplyToSection, BillingInvoiceAdjustmentFields } from './Components';
import { OptionMenuItems, OptionMenuItemTypes } from '@root/Components/Picker/OptionMenu';
import { useEvent, useToggle } from '@root/Services/EventEmitter';
import { useDisclosure } from '@mantine/hooks';
import { UploadInvoiceFileForm } from './UploadInvoiceFileForm';
import { BasicApi } from '@root/Services/BasicApi';
import { CompanyContextService } from '@root/Services/Customers/CompanyContext';
import { SidePanelContainer } from '@root/Design/SidePanel';
import { useNav } from '@root/Services/NavigationService';
import { Route } from '@root/Services/Router/RouteSerializer';
import { useLink } from '@root/Services/Router/Router';
import { AnchorButton, Clearfix, TooltipWhite } from '@root/Design/Primitives';
import { DatePicker } from '@mantine/dates';
import { naiveJsonCopy } from '@root/Services/QueryExpr';
import { LineItemImporter } from './LineItemImporter';

const adjustmentOperationLabels: Record<BillingInvoiceAdjustmentAdjustmentOperation, string> = {
    CustomLineItems: 'Add custom line items, imported from spreadsheet',
    ExistingAmount: 'Adjust line item costs by a specific amount',
    ExistingPercent: 'Adjust line item costs by a percent %',
    NewAmount: 'Add line items with a specific $ amount',
    NewPercentOfTotal: 'Add line items based on % of total invoice amount',
    NewPercentOfLineItems: "Add line items based on % of filtered line items' total",
    OmitLineItems: 'Remove line items based on filter criteria',
};

const adjustmentOptions = Object.values(BillingInvoiceAdjustmentAdjustmentOperation).map((operation) => ({
    value: operation,
    label: adjustmentOperationLabels[operation],
}));

const platformOptions = Object.values(BillingInvoiceAdjustmentPlatform).map((platform) => ({
    value: platform,
    label: platform,
}));

export function InvoiceDetailsForm(props: {
    model: BillingInvoiceAdminPanelModel;
    onClose: (didEdit: boolean) => void;
    billingInvoice: BillingInvoice;
}) {
    const [loading, setLoading] = useState(false);
    const fmtSvc = useDi(FormatService);
    const companyCtx = useDi(CompanyContextService);

    //Status
    const [statusUpdated, setStatusUpdated] = useState(false);
    const [status, setStatus] = useState(props.billingInvoice.Status);
    const [comments, setComments] = useState('');
    const [billingTerm, setBillingTerm] = useState(props.billingInvoice.BillingTerm ?? '');
    const statusOptions = ['Draft', 'Finalized', 'Sent', 'Paid', 'Voided', 'Deleted'];
    const [statusHistory, setStatusHistory] = useState<BillingInvoiceStatus[]>([]);
    const [saveReady, setSaveReady] = useState(false);
    const [canRun, setCanRun] = useState(true);
    const [reprocessButtonTooltip, setReprocessButtonTooltip] = useState('Run Invoice Ingestion');
    //Adjustments
    const [adjustmentsAdded, setAdjustmentsAdded] = useState(false);
    const [adjustments, setAdjustments] = useState<BillingInvoiceAdjustment[]>([]);
    const [statusCollapsed, setStatusCollapsed] = useState(true);
    const [adjustmentsCollapsed, setAdjustmentsCollapsed] = useState(false);
    const [statusSettingsBar, setStatusSettingsBar] = useState<string>('Status: ' + props.billingInvoice.Status?.toString());
    const [title, setTitle] = useState<string>(`Invoice Amount: ${fmtSvc.formatMoney(props.billingInvoice.Total!)}`);
    const [adjustmentedAmount, setAdjustmentedAmount] = useState<number>(0);

    //upload stuff
    const [UploadModalOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
    const [AddBulkAdjustmentsOpened, { open: openBulkAdjustments, close: closeBulkAdjustments }] = useDisclosure(false);
    const DiContainer = useDiComponent();
    const basicApi = useDi(BasicApi);
    const [downloadReady, setDownloadReady] = useState(props.billingInvoice.FileKey ? true : false);

    useEffect(() => {
        if (statusUpdated) {
            setSaveReady(true);
        }
        if (adjustmentsAdded) {
            setSaveReady(true);
        }
    }, [statusUpdated, adjustmentsAdded]);

    useEffect(() => {
        loadAdjustments();
        loadStatusHistory();
        checkCanRun();
    }, []);

    const loadAdjustments = () => {
        if (props.billingInvoice.Adjustments) {
            props.billingInvoice.Adjustments.sort((a, b) => a.Order! - b.Order!);
            setAdjustments(props.billingInvoice.Adjustments!);
            setAdjustmentsCollapsed(true);
        } else {
            setAdjustments([
                {
                    Description: '',
                    BillingInvoiceId: props.billingInvoice.Id,
                    Amount: 0,
                    Fields: [],
                    AdjustmentOperation: undefined,
                    Order: 1,
                },
            ]);
            setAdjustmentsCollapsed(false);
        }
    };

    const loadStatusHistory = async () => {
        const statusHistory = await getBillingInvoiceGetBillingInvoiceStatuses({ billingInvoiceId: props.billingInvoice.Id! });
        if (statusHistory) {
            setStatusHistory(statusHistory.sort((a, b) => new Date(b.ModifiedAt!).getTime() - new Date(a.ModifiedAt!).getTime()));
        }
    };

    const checkCanRun = async () => {
        let lastRunReq: Promise<InvoiceProcessingLogEntry | undefined> = Promise.resolve(undefined);
        companyCtx.withParentCompany(
            () =>
                (lastRunReq = getBillingInvoiceGetLastInvoiceUpdatedDate({
                    companyId: props.billingInvoice.CompanyId!,
                    month: props.billingInvoice.Month!,
                    year: props.billingInvoice.Year!,
                }))
        );
        const lastRun = await lastRunReq;
        const dateNow = new Date();
        const dateLastRun = !lastRun?.StartedAt ? null : new Date(lastRun?.StartedAt);
        const canRun = !dateLastRun ? true : props.billingInvoice.Status !== 'Finalized' && dateNow.getTime() - dateLastRun.getTime() > 3600000;
        setCanRun(canRun);
        setReprocessButtonTooltip(canRun ? 'Run Invoice Ingestion' : 'Invoice ingestion available every 1 hour');
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await Promise.all([handleStatusSave(), handleAdjustmentsSave()]);
        } finally {
            setLoading(false);
        }
        props.onClose(true);
    };

    const handleStatusSave = () =>
        new Promise<void>((resolve) => {
            if (statusUpdated) {
                const invoiceStatus: BillingInvoiceStatus = {
                    Comments: comments,
                    Status: status,
                    BillingInvoiceId: props.billingInvoice.Id,
                    Snapshot: props.billingInvoice.FinalizedSnapshot,
                };
                companyCtx.withParentCompany(() => {
                    postBillingInvoiceUpdateBillingInvoiceStatus(invoiceStatus).finally(resolve);
                });
            } else {
                resolve();
            }
        });

    const handleAdjustmentsSave = () =>
        new Promise<void>((resolve) => {
            if (adjustmentsAdded) {
                setLoading(true);
                companyCtx.withParentCompany(() => {
                    postBillingInvoiceSaveBillingInvoiceAdjustments(adjustments, { billingInvoiceId: props.billingInvoice.Id }).finally(resolve);
                });
            } else {
                resolve();
            }
        });

    // Status handlers
    const handleStatusChange = (value: string) => {
        setStatus(value as BillingInvoiceStatusProperty);
        setStatusSettingsBar('Status: ' + value);
        setStatusUpdated(true);
    };

    const handleCommentsChange = (e: any) => {
        setComments(e.target.value);
    };

    const handleBillingTermChange = (e: any) => {
        setBillingTerm(e.target.value);
    };

    // Adjustments handlers
    const handleAddAdjustment = () => {
        const nextOrder = adjustments.length === 0 ? 1 : Math.max(...adjustments.map((a) => a.Order ?? 0)) + 1;

        setAdjustments([
            ...adjustments,
            {
                Description: '',
                BillingInvoiceId: props.billingInvoice.Id,
                Amount: 0,
                Fields: [],
                AdjustmentOperation: undefined,
                Order: nextOrder,
            },
        ]);
    };

    const handleAdjustmentChange = (index: number, updater: (adjustment: BillingInvoiceAdjustment) => BillingInvoiceAdjustment) => {
        setAdjustments((prevAdjustments) => {
            const updatedAdjustments = prevAdjustments.map((adj, i) => (i === index ? updater(adj) : adj));
            return updatedAdjustments;
        });
        setAdjustmentsAdded(true);
    };

    const handleDeleteAdjustment = (index: number) => {
        setAdjustments((prevAdjustments) => {
            const updated = [...prevAdjustments];
            updated.splice(index, 1);
            return reorderAdjustments(updated);
        });
        setAdjustmentsAdded(true);
    };

    const handleCopyAdjustment = (index: number) => {
        setAdjustments((prevAdjustments) => {
            const adjustment = naiveJsonCopy(prevAdjustments[index]);
            adjustment.Id = undefined;
            const updated = [...prevAdjustments, adjustment];
            return reorderAdjustments(updated);
        });
        setAdjustmentsAdded(true);
    };

    const reorderAdjustments = (list: BillingInvoiceAdjustment[]) => {
        return list.map((adj, idx) => ({ ...adj, Order: idx + 1 }));
    };

    const moveItem = (from: number, to: number) => {
        setAdjustments((prevAdjustments) => {
            const updated = [...prevAdjustments];
            const [moved] = updated.splice(from, 1);
            updated.splice(to, 0, moved);
            return reorderAdjustments(updated);
        });
        setAdjustmentsAdded(true);
    };

    const handleMoveUp = (index: number) => {
        if (index > 0) moveItem(index, index - 1);
    };

    const handleMoveDown = (index: number) => {
        if (index < adjustments.length - 1) moveItem(index, index + 1);
    };

    useEffect(() => {
        if (adjustments.length > 0) {
            //check if applyTo is set
            const adjustmentsWithApplyTo = adjustments.filter((a) => a.ApplyTo !== undefined);
            //if applyTo is set, calculate the adjustment amount
            if (adjustmentsWithApplyTo.length > 0) {
                const random = Math.floor(Math.random() * 100);
                setAdjustmentedAmount(random);
            } else {
                setAdjustmentedAmount(0);
            }
        }
    }, [adjustments, adjustmentsAdded]);

    useEvent(props.model.adjustmentChanged, (value) => setAdjustmentsAdded(value));

    //don't need to do anything on modal close
    const onModalClose = useCallback(() => {
        setDownloadReady(true);
        closeAdd();
    }, []);

    const onAddBulkModalClose = useCallback(
        (didAdd: boolean) => {
            if (didAdd) {
                loadAdjustments();
            }
            closeBulkAdjustments();
        },
        [closeBulkAdjustments]
    );

    const downloadFile = useCallback(() => {
        const params = {
            invoiceId: props.billingInvoice.Id,
        };

        const fileName = `Cloud Invoice - ${props.billingInvoice.Year}-${props.billingInvoice.Month!.toString().padStart(2, '0')}`;
        basicApi.download(`${fileName}.pdf`, { url: '/BillingInvoice/DownloadInvoiceFile', method: 'GET', params }, 'Invoices');
    }, [props.billingInvoice.Id]);

    const reRunInvoice = async () => {
        setLoading(true);
        companyCtx.withParentCompany(() =>
            postBillingInvoiceRerunInvoiceProcessing(props.billingInvoice).finally(() => {
                setLoading(false);
                setCanRun(false);
                setTimeout(() => {
                    checkCanRun();
                }, 10000);
            })
        );
    };

    const { getRootUrl } = useNav();
    const id = props.billingInvoice.CompanyId ?? 0;
    const month = `${props.billingInvoice.Year}${props.billingInvoice.Month!.toString().padStart(2, '0')}01`;
    const route = [
        { name: 'manage-company', data: { id } },
        { name: 'FinOps', data: {} },
        { name: 'invoice-explorer', data: { month } },
    ] as Route;
    const url = getRootUrl(route);

    const baseAdjProps = {
        billingInvoice: props.billingInvoice,
        listLength: adjustments.length,
        model: props.model,
        onCopy: handleCopyAdjustment,
        onChange: handleAdjustmentChange,
        onDelete: handleDeleteAdjustment,
        onMoveUp: handleMoveUp,
        onMoveDown: handleMoveDown,
    };
    return (
        <Stack sx={{ height: '100%' }} spacing={0}>
            <Box px="lg" sx={{ flex: 1, overflow: 'auto', height: '100%' }}>
                {loading && <LoadingOverlay visible={true} />}
                <Modal opened={UploadModalOpened} closeOnClickOutside onClose={closeAdd} title="Upload InvoiceFile">
                    <DiContainer>
                        <UploadInvoiceFileForm invoiceId={props.billingInvoice.Id!} onClose={onModalClose}></UploadInvoiceFileForm>
                    </DiContainer>
                </Modal>

                {/* Invoice Header */}
                <Box py="md">
                    <Group position="apart">
                        <Title order={4}>{title}</Title>
                        <Group position="apart">
                            <Tooltip label={props.billingInvoice.FileKey ? 'Download Invoice' : 'No Invoice File'} withArrow>
                                <Button variant="outline" onClick={downloadFile} disabled={!downloadReady}>
                                    <Download />
                                </Button>
                            </Tooltip>
                            <Tooltip
                                label={props.billingInvoice.Status !== 'Finalized' ? 'Upload Invoice' : "Can't Upload File to finalized invoice"}
                                withArrow
                            >
                                <Button variant="outline" onClick={openAdd} disabled={props.billingInvoice.Status === 'Finalized'}>
                                    <Upload />
                                </Button>
                            </Tooltip>
                            <Tooltip label={'View in Invoice Explorer'} withArrow>
                                <Button component="a" target="_blank" variant="outline" href={url}>
                                    <Eye />
                                </Button>
                            </Tooltip>

                            <Tooltip label={reprocessButtonTooltip} withArrow>
                                <Button onClick={reRunInvoice} variant={canRun ? 'outline' : 'default'} disabled={!canRun}>
                                    <CaretRight />
                                </Button>
                            </Tooltip>
                        </Group>
                    </Group>
                </Box>
                {/* Status */}
                <SettingsSection title={statusSettingsBar} collapsed={statusCollapsed}>
                    <SettingsSectionItem>
                        <SettingsSectionItemHeader>
                            <SettingsSectionItemHeaderLabel>Invoice Status</SettingsSectionItemHeaderLabel>
                        </SettingsSectionItemHeader>
                        <SettingsSectionItemBody>
                            <SettingsInputRow>
                                <SettingsLabel>Update Status</SettingsLabel>
                                <Select
                                    data={statusOptions.map((i) => {
                                        return { value: i, label: i };
                                    })}
                                    value={status}
                                    onChange={handleStatusChange}
                                ></Select>
                            </SettingsInputRow>
                            <SettingsInputRow>
                                <SettingsLabel>Billing Term</SettingsLabel>
                                <TextInput sx={{ width: 250 }} value={billingTerm} onChange={handleBillingTermChange}></TextInput>
                            </SettingsInputRow>
                            <SettingsSectionBodyDivider />
                            <SettingsInputStack>
                                <SettingsLabel>Comments</SettingsLabel>
                                <Textarea value={comments} onChange={handleCommentsChange}></Textarea>
                            </SettingsInputStack>
                            {/* Status History */}
                            {statusHistory && statusHistory.length > 0 && (
                                <SettingsSectionItemHeaderLabel>
                                    <Button variant="subtle" onClick={() => setStatusCollapsed(!statusCollapsed)}>
                                        {statusCollapsed ? 'Show History' : 'Hide History'}
                                    </Button>
                                    {!statusCollapsed && (
                                        <SettingsSection title="Status History">
                                            {statusHistory.map((status, index) => (
                                                <SettingsSectionItem key={index}>
                                                    <SettingsSectionItemHeader>
                                                        <SettingsSectionItemHeaderLabel>{status.Status}</SettingsSectionItemHeaderLabel>
                                                    </SettingsSectionItemHeader>
                                                    <SettingsSectionItemBody>
                                                        <Text>Date Changed: {fmtSvc.toShortDate(fmtSvc.toLocalDate(status.ModifiedAt!))}</Text>
                                                        <Text>Comments: {status.Comments}</Text>
                                                    </SettingsSectionItemBody>
                                                </SettingsSectionItem>
                                            ))}
                                        </SettingsSection>
                                    )}
                                </SettingsSectionItemHeaderLabel>
                            )}
                        </SettingsSectionItemBody>
                    </SettingsSectionItem>
                </SettingsSection>

                <Space h="md" />
                <SettingsSection title={'Adjustments'} collapsed={adjustmentsCollapsed}>
                    {adjustments.map((adjustment, index) => (
                        <InvoiceAdjustment key={index} {...{ ...baseAdjProps, adjustment, index, listLength: adjustments.length }} />
                    ))}
                    <SettingsSectionItem style={{ flex: 1 }} anchor onClick={handleAddAdjustment}>
                        <AnchorButton size="sm" icon={<Plus size={16} />} text={`Add ${adjustments.length > 0 ? 'another' : 'an'} adjustment`} />
                    </SettingsSectionItem>
                </SettingsSection>
                <Space h="md" />
            </Box>
            {/* Save */}
            <div
                style={{
                    backgroundColor: 'white',
                    padding: 20,
                    boxShadow: '0 0px 10px rgba(0,0,0,0.1)',
                }}
            >
                <Group position="right" pr="md">
                    <Button onClick={() => props.onClose(false)} variant="outline">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={!saveReady}>
                        Save
                    </Button>
                </Group>
            </div>
        </Stack>
    );
}

interface InvoiceAdjustmentProps {
    adjustment: BillingInvoiceAdjustment;
    billingInvoice: BillingInvoice;
    index: number;
    listLength: number;
    model: BillingInvoiceAdminPanelModel;
    onCopy: (index: number) => void;
    onChange: (index: number, updater: (adjustment: BillingInvoiceAdjustment) => BillingInvoiceAdjustment) => void;
    onDelete: (index: number) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
}
function InvoiceAdjustment(props: InvoiceAdjustmentProps) {
    const fmtSvc = useFmtSvc();
    const { adjustment, billingInvoice, index, listLength, model, onCopy, onChange, onDelete, onMoveUp, onMoveDown } = props;
    const [expanded, { toggle }] = useToggle(!adjustment.Id);
    const isExactAmount = (adjustment: BillingInvoiceAdjustment) =>
        adjustment.AdjustmentOperation === 'NewAmount' || adjustment.AdjustmentOperation === 'ExistingAmount';
    const handleCopy = useCallback(() => onCopy(index), [index, onCopy]);
    const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);
    const handleOnMoveUp = useCallback(() => onMoveUp(index), [index, onMoveUp]);
    const handleOnMoveDown = useCallback(() => onMoveDown(index), [index, onMoveDown]);
    const handleAdjustmentChange = useCallback(
        <T extends keyof BillingInvoiceAdjustment>(field: T, value: BillingInvoiceAdjustment[T]) => {
            onChange(index, (adjustment: BillingInvoiceAdjustment) => {
                const updatedAdjustment = { ...adjustment, [field]: value };

                if (!isExactAmount(updatedAdjustment)) {
                    updatedAdjustment.Platform = undefined;
                } else if (updatedAdjustment.AdjustmentOperation !== 'CustomLineItems') {
                    updatedAdjustment.ImportFields = undefined;
                }
                return updatedAdjustment;
            });
        },
        [index, onChange]
    );
    const options = useMemo(
        () =>
            [
                { label: 'Copy Adjustment', onClick: handleCopy, icon: <Copy size={16} /> },
                ...(index > 0 ? [{ label: 'Move Up', onClick: handleOnMoveUp, icon: <ArrowUp size={16} /> }] : []),
                ...(index < listLength - 1 ? [{ label: 'Move Down', onClick: handleOnMoveDown, icon: <ArrowDown size={16} /> }] : []),
                { label: 'Delete Adjustment', onClick: handleDelete, icon: <Trash size={16} /> },
            ] as OptionMenuItemTypes[],
        [index, listLength, handleCopy, handleDelete, handleOnMoveUp, handleOnMoveDown]
    );
    const amtType = isExactAmount(adjustment) ? '$' : '%';
    const adjOp = adjustment.AdjustmentOperation ?? 'NewAmount';

    const showPlatformPicker = ['NewAmount', 'ExistingAmount'].includes(adjOp);
    const showAdjustmentDates = ['NewAmount', 'NewPercentOfLineItems', 'NewPercentOfTotal'].includes(adjOp);
    const showAmount = ['NewAmount', 'NewPercentOfTotal', 'NewPercentOfLineItems', 'ExistingAmount', 'ExistingPercent'].includes(adjOp);
    const showApplyTo = ['NewPercentOfTotal', 'NewPercentOfLineItems', 'ExistingAmount', 'ExistingPercent', 'OmitLineItems'].includes(adjOp);
    const showFieldInput = ['NewAmount', 'NewPercentOfTotal', 'NewPercentOfLineItems'].includes(adjOp);
    const showImport = ['CustomLineItems'].includes(adjOp);
    return (
        <SettingsSectionItem>
            <SettingsSectionItemHeader>
                <Group spacing={4}>
                    <ActionIcon onClick={toggle}>{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</ActionIcon>

                    <SettingsSectionItemHeaderLabel>Adjustment {index + 1}</SettingsSectionItemHeaderLabel>
                </Group>
                <Menu withinPortal position="bottom-end" shadow="md">
                    <Menu.Target>
                        <ActionIcon variant="subtle">
                            <DotsVertical size={16} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <OptionMenuItems options={options} close={() => {}} />
                    </Menu.Dropdown>
                </Menu>
            </SettingsSectionItemHeader>
            <SettingsSectionItemBody>
                <SettingsInputStack>
                    <Group noWrap>
                        <Select
                            data={adjustmentOptions}
                            value={adjustment.AdjustmentOperation}
                            onChange={(value) => handleAdjustmentChange('AdjustmentOperation', value as BillingInvoiceAdjustmentAdjustmentOperation)}
                            placeholder="Select Adjustment Type"
                            sx={{ flex: 1 }}
                        ></Select>
                    </Group>
                </SettingsInputStack>
                <SettingsInputStack>
                    <SettingsLabel>Description</SettingsLabel>
                    <Textarea value={adjustment.Description!} onChange={(e) => handleAdjustmentChange('Description', e.target.value)}></Textarea>
                </SettingsInputStack>
            </SettingsSectionItemBody>
            {!expanded ? null : (
                <>
                    <SettingsSectionBodyDivider />
                    {!adjustment.AdjustmentOperation ? null : (
                        <>
                            <SettingsSectionItemBody>
                                {showPlatformPicker ? (
                                    <SettingsInputRow>
                                        <SettingsLabel>Cloud Platform</SettingsLabel>
                                        <Select
                                            data={platformOptions}
                                            value={adjustment.Platform}
                                            onChange={(value) => handleAdjustmentChange('Platform', value as BillingInvoiceAdjustmentPlatform)}
                                            style={{ width: '250px' }}
                                        ></Select>
                                    </SettingsInputRow>
                                ) : null}
                                {!showAmount ? null : (
                                    <SettingsInputRow>
                                        <SettingsLabel>Amount ({amtType})</SettingsLabel>
                                        <NumberInput
                                            value={adjustment.Amount!}
                                            precision={5}
                                            onChange={(value) => handleAdjustmentChange('Amount', value || 0)}
                                        ></NumberInput>
                                    </SettingsInputRow>
                                )}
                                <Box>
                                    {!showAdjustmentDates ? null : (
                                        <>
                                            <SettingsInputRow>
                                                <SettingsLabel>Adjustment Start Date</SettingsLabel>
                                                <DatePicker
                                                    value={adjustment.StartDate ? new Date(adjustment.StartDate) : null}
                                                    onChange={(date) => handleAdjustmentChange('StartDate', date as unknown as string)}
                                                />
                                            </SettingsInputRow>
                                            <SettingsInputRow>
                                                <SettingsLabel>Adjustment End Date</SettingsLabel>
                                                <DatePicker
                                                    value={adjustment.EndDate ? new Date(adjustment.EndDate) : null}
                                                    onChange={(date) => handleAdjustmentChange('EndDate', date as unknown as string)}
                                                />
                                            </SettingsInputRow>
                                        </>
                                    )}
                                </Box>
                            </SettingsSectionItemBody>
                            {!showApplyTo ? null : (
                                <>
                                    <SettingsSectionBodyDivider />
                                    <SettingsSectionItemBody style={{ margin: 'calc(-1 * var(--settings-body-padding))' }}>
                                        <ApplyToSection
                                            adjustment={adjustment}
                                            billingInvoice={billingInvoice}
                                            model={model}
                                            onChanges={(updated) => handleAdjustmentChange('ApplyTo', updated.ApplyTo)}
                                        />
                                    </SettingsSectionItemBody>
                                </>
                            )}
                            {!showFieldInput ? null : (
                                <>
                                    <SettingsSectionBodyDivider />
                                    <SettingsSectionItemBody style={{ margin: 'calc(-1 * var(--settings-body-padding))' }}>
                                        <BillingInvoiceAdjustmentFields
                                            invoice={billingInvoice}
                                            fields={adjustment.Fields!}
                                            onChange={(fields) => handleAdjustmentChange('Fields', fields || [])}
                                        />
                                    </SettingsSectionItemBody>
                                </>
                            )}
                            {!showImport ? null : (
                                <SettingsSectionItemBody style={{ marginTop: 'calc(-2 * var(--settings-body-padding))' }}>
                                    <LineItemImporter
                                        invoice={billingInvoice}
                                        onChange={(v) => handleAdjustmentChange('ImportFields', v)}
                                        value={adjustment.ImportFields ?? null}
                                    />
                                </SettingsSectionItemBody>
                            )}
                            <SettingsSectionBodyDivider />
                            <SettingsSectionItemBody>
                                <SettingsInputStack>
                                    <TooltipWhite label="If checked, given that the next billing period's invoice does not exist yet, when the next billing period's invoice is first created, this adjustment will be automatically copied to the new invoice.">
                                        <Checkbox
                                            checked={adjustment.CopyToNextMonth}
                                            onChange={(e) => handleAdjustmentChange('CopyToNextMonth', e.target.checked ? true : false)}
                                            label="Auto-copy this adjustment to next month's invoice"
                                        />
                                    </TooltipWhite>
                                </SettingsInputStack>
                            </SettingsSectionItemBody>
                            {false && (
                                <>
                                    <SettingsSectionBodyDivider />
                                    <SettingsSectionItemBody>
                                        <SettingsInputRow>
                                            <SettingsLabel>Adjustment Amount</SettingsLabel>
                                            <SettingsLabel>{fmtSvc.formatMoney(0)}</SettingsLabel>
                                        </SettingsInputRow>
                                    </SettingsSectionItemBody>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </SettingsSectionItem>
    );
}
